import Janus, { type JanusJS } from 'janus-gateway';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { useAuth } from './AuthContext';
import { useOperationalTab } from './OperationalTabContext';
import {
  addOperationalBreadcrumb,
  captureFrontendException,
  captureOperationalIssue,
  setSipScopeContext,
} from '../observability/sentry';
import api from '../services/api';
import { SIP_INTERACTION_ALREADY_ANSWERED_ERROR } from '../constants/sipInteraction';
import {
  getApiErrorField,
  getApiErrorMessage as resolveApiErrorMessage,
  getApiErrorStatus,
} from '../utils/errors';
import { describeJanusAuth, resolveJanusAuthOptions, type JanusAuthPayload } from '../utils/janusAuth';
import { applyTurnIceServersRefresh, type TurnIceServersRefreshPayload } from '../utils/turnIceServers';

export type SipExtensionStatus = 'idle' | 'registering' | 'registered' | 'incoming' | 'calling' | 'in_call' | 'error';
export type SipIncomingRecoveryState = 'idle' | 'waiting_offer' | 'retrying_delivery';
export type SipHoldDirection = 'sendonly' | 'recvonly' | 'inactive';
export type SipConnectionState = 'unknown' | 'checking' | 'connected' | 'recovering' | 'disconnected' | 'blocked_auth' | 'error';

interface SipIncomingCall {
  receivedAt: string;
  hasOffer: boolean;
  event: string;
  sipCallId: string | null;
  callPublicId: string | null;
  tenantPublicId: string | null;
}

interface SipExtensionContextValue {
  isEligible: boolean;
  status: SipExtensionStatus;
  connectionState: SipConnectionState;
  connectionReasonCode: string | null;
  lastVerifiedAt: string | null;
  isRegistered: boolean;
  isRegistering: boolean;
  lastError: string | null;
  sessionId: string | null;
  incomingCall: SipIncomingCall | null;
  incomingRecoveryState: SipIncomingRecoveryState;
  remoteAudioStream: MediaStream | null;
  isMuted: boolean;
  startRegistration: () => Promise<void>;
  stopRegistration: () => Promise<void>;
  acceptIncoming: (
    callId?: string,
    options?: { retryIncomingDelivery?: boolean; deferUntilOffer?: boolean },
  ) => Promise<boolean>;
  rejectIncoming: () => Promise<boolean>;
  hangup: () => Promise<void>;
  hold: (direction?: SipHoldDirection) => Promise<boolean>;
  unhold: () => Promise<boolean>;
  mute: () => Promise<boolean>;
  unmute: () => Promise<boolean>;
  reconcileTerminalCallState: (callPublicId?: string | null, options?: { force?: boolean }) => void;
  clearIncoming: () => void;
}

interface SipSessionStartResponse extends JanusAuthPayload {
  sessionId: string;
  janusWsUrl: string;
  iceServers?: RTCIceServer[];
  iceServersExpiresAt?: number | null;
  sipUsername: string;
  sipAuthuser?: string;
  sipSecret: string;
  sipProxy: string;
  sipUserAgent?: string;
  sipExtension?: string;
}

interface SipSessionReadinessResponse {
  status: 'ok' | 'degraded';
  sessionStatus: 'ok' | 'missing';
  endpointStatus: 'ok' | 'missing' | 'invalid';
  sipContactStatus: 'present' | 'missing' | 'unavailable' | 'unknown';
  extension: string | null;
  reasonCode: string | null;
  verifiedAt: string | null;
}

interface SipPluginResult {
  event?: unknown;
  call_id?: unknown;
  code?: unknown;
  reason?: unknown;
  headers?: unknown;
}

interface SipPluginMessage {
  error?: unknown;
  result?: SipPluginResult;
  plugindata?: {
    data?: {
      error?: unknown;
      error_reason?: unknown;
      result?: SipPluginResult;
    };
  };
}

const SipExtensionContext = createContext<SipExtensionContextValue | undefined>(undefined);
const SIP_INCOMING_HEADER_PREFIX = 'X-CT-';
const SIP_INCOMING_HEADER_CALL_ID = 'X-CT-Call-Id';
const SIP_INCOMING_HEADER_TENANT_ID = 'X-CT-Tenant-Id';
const SIP_KEEPALIVE_INTERVAL_MS = 20_000;
const SIP_KEEPALIVE_FAILURE_THRESHOLD = 3;
const SIP_RECOVERY_COOLDOWN_MS = 15_000;
const SIP_CONTACT_VERIFICATION_GRACE_MS = 12_000;
const SIP_SUSPENSION_DRIFT_THRESHOLD_MS = 45_000;

function isEndpointEligibleForSip(endpoint: {
  isActive?: boolean;
  endpointType?: string | null;
  extension?: string | null;
} | null | undefined): boolean {
  if (!endpoint?.isActive) {
    return false;
  }
  if (endpoint.endpointType !== 'internal_extension' && endpoint.endpointType !== 'external_extension') {
    return false;
  }
  return Boolean(String(endpoint.extension || '').trim());
}

function getApiErrorMessage(error: unknown, fallback: string): string {
  return resolveApiErrorMessage(error, fallback);
}

function getHttpStatusCode(error: unknown): number | null {
  return getApiErrorStatus(error);
}

function getIncomingHeaderValue(headers: unknown, headerName: string): string | null {
  if (!headers || typeof headers !== 'object') {
    return null;
  }
  const target = String(headerName || '').trim().toLowerCase();
  if (!target) {
    return null;
  }
  for (const [key, value] of Object.entries(headers as Record<string, unknown>)) {
    if (String(key || '').trim().toLowerCase() !== target) {
      continue;
    }
    const normalizedValue = normalizeIncomingHeaderValue(value);
    return normalizedValue || null;
  }
  return null;
}

function normalizeIncomingHeaderValue(value: unknown): string | null {
  if (value == null) {
    return null;
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    const normalized = String(value).trim();
    return normalized || null;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const normalized = normalizeIncomingHeaderValue(item);
      if (normalized) {
        return normalized;
      }
    }
    return null;
  }
  if (typeof value === 'object') {
    const objectValue = value as Record<string, unknown>;
    for (const key of ['value', 'raw', 'text']) {
      if (key in objectValue) {
        const normalized = normalizeIncomingHeaderValue(objectValue[key]);
        if (normalized) {
          return normalized;
        }
      }
    }
  }
  const fallback = String(value).trim();
  return fallback || null;
}

function getIncomingHeadersDebugSnapshot(headers: unknown): Record<string, string> | null {
  if (!headers || typeof headers !== 'object') {
    return null;
  }
  const snapshot: Record<string, string> = {};
  for (const [key, value] of Object.entries(headers as Record<string, unknown>)) {
    const normalized = normalizeIncomingHeaderValue(value);
    if (normalized) {
      snapshot[String(key)] = normalized;
      continue;
    }
    try {
      snapshot[String(key)] = JSON.stringify(value);
    } catch {
      snapshot[String(key)] = String(value ?? '');
    }
  }
  return snapshot;
}

function serializeDebugPayload(value: unknown): string | null {
  if (value == null) {
    return null;
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function isDocumentDiscarded(documentValue: Document): boolean {
  return (
    'wasDiscarded' in documentValue
    && (documentValue as Document & { wasDiscarded?: boolean }).wasDiscarded === true
  );
}

type SipLifecycleCancelledError = Error & {
  code: 'sip_lifecycle_cancelled';
};

/**
 * Build a non-fatal error used to short-circuit stale Janus callbacks.
 *
 * Janus lifecycle callbacks can still fire after an intentional teardown or a
 * newer recovery attempt replaced the active instance. Those late callbacks
 * should end the in-flight bootstrap quietly instead of reintroducing stale
 * state or surfacing a frontend error.
 */
function createSipLifecycleCancelledError(message: string): SipLifecycleCancelledError {
  const error = new Error(message) as SipLifecycleCancelledError;
  error.name = 'SipLifecycleCancelledError';
  error.code = 'sip_lifecycle_cancelled';
  return error;
}

function isSipLifecycleCancelledError(error: unknown): error is SipLifecycleCancelledError {
  return (
    error instanceof Error
    && 'code' in error
    && (error as { code?: string }).code === 'sip_lifecycle_cancelled'
  );
}

/**
 * Global SIP extension provider.
 *
 * Responsibilities:
 * - eager SIP registration after login when endpoint is eligible;
 * - expose registration/incoming status for global UI (FAB + drawer controls);
 * - expose remote SIP media stream so call UI can visualize live audio activity (orb);
 * - own Janus SIP plugin lifecycle and keepalive for user-scoped SIP session.
 */
export function TelephonySipProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  const { canOperateInThisTab } = useOperationalTab();
  const [status, setStatus] = useState<SipExtensionStatus>('idle');
  const [connectionState, setConnectionState] = useState<SipConnectionState>('unknown');
  const [connectionReasonCode, setConnectionReasonCode] = useState<string | null>(null);
  const [lastVerifiedAt, setLastVerifiedAt] = useState<string | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [incomingCall, setIncomingCall] = useState<SipIncomingCall | null>(null);
  const [incomingRecoveryState, setIncomingRecoveryState] = useState<SipIncomingRecoveryState>('idle');
  const [remoteAudioStream, setRemoteAudioStream] = useState<MediaStream | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const janusRef = useRef<Janus | null>(null);
  // Same array instance handed to `new Janus(...)`: Janus.js reuses it for every
  // new PeerConnection, so heartbeat TURN renewals are applied in place.
  const janusIceServersRef = useRef<RTCIceServer[] | null>(null);
  const sipHandleRef = useRef<JanusJS.PluginHandle | null>(null);
  const janusInitDoneRef = useRef(false);
  const keepaliveRef = useRef<number | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const incomingJsepRef = useRef<JanusJS.JSEP | null>(null);
  const pendingAutoAcceptRef = useRef(false);
  const acceptInFlightRef = useRef<Promise<boolean> | null>(null);
  const startInFlightRef = useRef(false);
  const sessionIdRef = useRef<string | null>(null);
  const registrationIdentityRef = useRef<string | null>(null);
  const registrationBootstrapBlockedRef = useRef(false);
  const statusRef = useRef<SipExtensionStatus>('idle');
  const keepaliveFailureCountRef = useRef(0);
  const heartbeatLastRunAtRef = useRef<number | null>(null);
  const registrationStartedAtRef = useRef<number | null>(null);
  const registrationConfirmedAtRef = useRef<number | null>(null);
  const recoveryInFlightRef = useRef(false);
  const recoveryLastStartedAtRef = useRef(0);
  const activeJanusLifecycleVersionRef = useRef<number | null>(null);
  const janusLifecycleVersionCounterRef = useRef(0);
  const intentionallyDestroyedJanusLifecycleVersionRef = useRef<number | null>(null);
  const probeSipSessionHealthRef = useRef<(reason: string) => Promise<void>>(async () => {});
  const discardedRecoveryHandledRef = useRef(false);
  const acceptedInteractionCandidateRef = useRef<SipIncomingCall | null>(null);
  const persistedAnsweredCallIdsRef = useRef<Set<string>>(new Set());
  const trackedCallPublicIdRef = useRef<string | null>(null);
  const lastObservedSipSignatureRef = useRef<string | null>(null);

  const endpointEligible = useMemo(
    () => isEndpointEligibleForSip(user?.telephonyEndpoint),
    [user?.telephonyEndpoint],
  );
  const registrationIdentity = useMemo(() => {
    if (!isAuthenticated || !endpointEligible || !canOperateInThisTab) {
      return null;
    }
    const endpointType = String(user?.telephonyEndpoint?.endpointType || '').trim();
    const extension = String(user?.telephonyEndpoint?.extension || '').trim();
    return `${String(user?.id || '')}:${endpointType}:${extension}`;
  }, [
    canOperateInThisTab,
    endpointEligible,
    isAuthenticated,
    user?.id,
    user?.telephonyEndpoint?.endpointType,
    user?.telephonyEndpoint?.extension,
  ]);

  const isRegistered = status === 'registered' || status === 'incoming' || status === 'calling' || status === 'in_call';
  const isRegistering = status === 'registering';
  const isEligible = isAuthenticated && endpointEligible && canOperateInThisTab;

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  useEffect(() => {
    setSipScopeContext({
      extension: user?.telephonyEndpoint?.extension || null,
      sessionId,
      status,
      connectionState,
      reasonCode: connectionReasonCode,
      lastVerifiedAt,
      incomingRecoveryState,
      canOperateInThisTab,
      isEligible,
    });
  }, [
    canOperateInThisTab,
    connectionReasonCode,
    connectionState,
    incomingRecoveryState,
    isEligible,
    lastVerifiedAt,
    sessionId,
    status,
    user?.telephonyEndpoint?.extension,
  ]);

  useEffect(() => {
    const nextSignature = [
      status,
      connectionState,
      connectionReasonCode || 'none',
      incomingRecoveryState,
      sessionId || 'none',
      user?.telephonyEndpoint?.extension || 'none',
    ].join('|');
    if (lastObservedSipSignatureRef.current === nextSignature) {
      return;
    }
    lastObservedSipSignatureRef.current = nextSignature;
    addOperationalBreadcrumb({
      category: 'sip.state',
      message: 'SIP state updated',
      level: connectionState === 'error' || status === 'error' ? 'warning' : 'info',
      data: {
        extension: user?.telephonyEndpoint?.extension || null,
        sessionId,
        status,
        connectionState,
        reasonCode: connectionReasonCode,
        incomingRecoveryState,
        isEligible,
        canOperateInThisTab,
      },
    });
  }, [
    canOperateInThisTab,
    connectionReasonCode,
    connectionState,
    incomingRecoveryState,
    isEligible,
    sessionId,
    status,
    user?.telephonyEndpoint?.extension,
  ]);
  const sipDebugEnabled = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    return params.has('callControlDebug') || window.localStorage.getItem('ct_call_control_debug') === '1';
  }, []);

  const logSipDebug = useCallback(
    (message: string, data?: Record<string, unknown>) => {
      if (!sipDebugEnabled) return;
      if (data) {
        console.info('[SIP]', message, data);
        return;
      }
      console.info('[SIP]', message);
    },
    [sipDebugEnabled],
  );

  const refreshJanusIceServers = useCallback(
    (payload: TurnIceServersRefreshPayload | null | undefined, source: string) => {
      const result = applyTurnIceServersRefresh(janusIceServersRef.current, payload);
      if (result.applied) {
        // Never log the credential itself.
        logSipDebug('TURN credentials renewed', {
          source,
          serverCount: result.serverCount,
          expiresAt: result.expiresAt,
        });
      }
    },
    [logSipDebug],
  );

  const applyConnectionState = useCallback((
    nextState: SipConnectionState,
    options?: {
      reasonCode?: string | null;
      verifiedAt?: string | null;
    },
  ) => {
    setConnectionState(nextState);
    setConnectionReasonCode(options?.reasonCode ?? null);
    if (options && 'verifiedAt' in options) {
      setLastVerifiedAt(options.verifiedAt ?? null);
    }
  }, []);

  const scheduleSipHealthProbe = useCallback((reason: string, delayMs = 0) => {
    if (typeof window === 'undefined') {
      return;
    }
    window.setTimeout(() => {
      void probeSipSessionHealthRef.current(reason);
    }, delayMs);
  }, []);

  const ensureAudioElement = useCallback(() => {
    if (audioElementRef.current) {
      return audioElementRef.current;
    }
    const element = document.createElement('audio');
    element.autoplay = true;
    element.setAttribute('playsinline', 'true');
    element.style.display = 'none';
    document.body.appendChild(element);
    audioElementRef.current = element;
    return element;
  }, []);

  const cleanupAudio = useCallback(() => {
    if (remoteStreamRef.current) {
      remoteStreamRef.current.getTracks().forEach((track) => track.stop());
      remoteStreamRef.current = null;
    }
    setRemoteAudioStream(null);
    if (audioElementRef.current) {
      try {
        audioElementRef.current.pause();
      } catch {
        // no-op
      }
      audioElementRef.current.srcObject = null;
      if (audioElementRef.current.parentNode) {
        audioElementRef.current.parentNode.removeChild(audioElementRef.current);
      }
      audioElementRef.current = null;
    }
  }, []);

  /**
   * Stop local microphone capture tracks that were attached to the SIP peer connection.
   *
   * Registration can stay alive after a call ends, but the browser should stop
   * reporting the microphone as in use once the active dialog is gone.
   */
  const cleanupLocalAudioSenders = useCallback(() => {
    const pc: RTCPeerConnection | null = sipHandleRef.current?.webrtcStuff?.pc ?? null;
    if (!pc || typeof pc.getSenders !== 'function') {
      return;
    }
    pc.getSenders().forEach((sender) => {
      if (sender.track?.kind !== 'audio') {
        return;
      }
      try {
        sender.track.stop();
      } catch {
        // no-op
      }
    });
  }, []);

  /**
   * Clear the local SIP call state while keeping the Janus registration alive.
   *
   * This is used both for normal Janus hangup notifications and for external
   * reconciliation when the backend/websocket already knows the call ended but
   * the SIP plugin did not emit the expected terminal event.
   */
  const clearTrackedCallState = useCallback((nextStatus: SipExtensionStatus = 'registered') => {
    incomingJsepRef.current = null;
    acceptedInteractionCandidateRef.current = null;
    pendingAutoAcceptRef.current = false;
    acceptInFlightRef.current = null;
    trackedCallPublicIdRef.current = null;
    setIncomingRecoveryState('idle');
    setIncomingCall(null);
    setIsMuted(false);
    cleanupLocalAudioSenders();
    cleanupAudio();
    statusRef.current = nextStatus;
    setStatus(nextStatus);
  }, [cleanupAudio, cleanupLocalAudioSenders]);

  const stopKeepalive = useCallback(() => {
    if (keepaliveRef.current) {
      window.clearInterval(keepaliveRef.current);
      keepaliveRef.current = null;
    }
  }, []);

  const stopRegistration = useCallback(async (options?: {
    preserveError?: boolean;
    nextStatus?: SipExtensionStatus;
  }) => {
    logSipDebug('stop registration requested', {
      hadSession: Boolean(sessionIdRef.current),
      hadHandle: Boolean(sipHandleRef.current),
      hadJanus: Boolean(janusRef.current),
    });
    stopKeepalive();
    pendingAutoAcceptRef.current = false;
    acceptInFlightRef.current = null;
    incomingJsepRef.current = null;
    acceptedInteractionCandidateRef.current = null;
    trackedCallPublicIdRef.current = null;
    setIncomingCall(null);
    setIncomingRecoveryState('idle');
    setIsMuted(false);

    const activeSessionId = sessionIdRef.current;
    if (sipHandleRef.current) {
      try {
        sipHandleRef.current.send({ message: { request: 'hangup' } });
      } catch {
        // no-op
      }
      try {
        sipHandleRef.current.send({ message: { request: 'unregister' } });
      } catch {
        // no-op
      }
      try {
        sipHandleRef.current.detach();
      } catch {
        // no-op
      }
      sipHandleRef.current = null;
    }
    const activeJanusLifecycleVersion = activeJanusLifecycleVersionRef.current;
    if (janusRef.current) {
      intentionallyDestroyedJanusLifecycleVersionRef.current = activeJanusLifecycleVersion;
      try {
        janusRef.current.destroy({});
      } catch {
        // no-op
      }
      janusRef.current = null;
    }
    janusIceServersRef.current = null;
    activeJanusLifecycleVersionRef.current = null;
    cleanupLocalAudioSenders();
    cleanupAudio();
    setSessionId(null);
    sessionIdRef.current = null;
    heartbeatLastRunAtRef.current = null;
    registrationStartedAtRef.current = null;
    registrationConfirmedAtRef.current = null;
    const nextStatus = options?.nextStatus || 'idle';
    statusRef.current = nextStatus;
    setStatus(nextStatus);
    if (!options?.preserveError) {
      setLastError(null);
    }

    if (!activeSessionId) {
      logSipDebug('stop registration completed (no active backend session)');
      return;
    }
    try {
      await api.post('/api/calls/sip_session_stop/', { session_id: activeSessionId });
      logSipDebug('backend sip_session_stop success', { sessionId: activeSessionId });
    } catch {
      // Best-effort cleanup only.
      logSipDebug('backend sip_session_stop failed (best-effort)');
    }
  }, [cleanupAudio, cleanupLocalAudioSenders, logSipDebug, stopKeepalive]);

  const initJanus = useCallback(
    () =>
      new Promise<void>((resolve) => {
        if (janusInitDoneRef.current) {
          resolve();
          return;
        }
        Janus.init({
          debug: false,
          callback: () => {
            janusInitDoneRef.current = true;
            resolve();
          },
        });
      }),
    [],
  );

  const requestMicrophonePermission = useCallback(async () => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      throw new Error('Microphone access is not available in this browser.');
    }
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((track) => track.stop());
  }, []);

  const waitForIncomingOffer = useCallback(async (timeoutMs: number): Promise<boolean> => {
    if (incomingJsepRef.current) {
      return true;
    }
    return new Promise<boolean>((resolve) => {
      const startedAt = Date.now();
      const timer = window.setInterval(() => {
        if (incomingJsepRef.current) {
          window.clearInterval(timer);
          resolve(true);
          return;
        }
        if (Date.now() - startedAt >= timeoutMs) {
          window.clearInterval(timer);
          resolve(false);
        }
      }, 100);
    });
  }, []);

  const requestIncomingDeliveryRetry = useCallback(
    async (callId: string, currentSessionId: string): Promise<{ ok: boolean; error?: string }> => {
      try {
        const response = await api.post(`/api/calls/${callId}/session_activate/`, {
          mode: 'interact',
          session_id: currentSessionId,
        });
        const activationStatus = String(response?.data?.status || '').trim().toLowerCase();
        logSipDebug('session_activate response', {
          callId,
          activationStatus: activationStatus || 'unknown',
          statusCode: response.status,
          reason: response?.data?.reason || null,
        });
        if (
          activationStatus === 'activated'
          || activationStatus === 'already_activated'
          || activationStatus === 'waiting_for_sip_contact'
        ) {
          return { ok: true };
        }
        return {
          ok: false,
          error: String(response?.data?.reason || response?.data?.error || 'incoming_delivery_retry_not_applied'),
        };
      } catch (error: unknown) {
        const message = getApiErrorMessage(error, 'Could not retry incoming call delivery.');
        logSipDebug('session_activate failed', {
          callId,
          error: message,
        });
        return { ok: false, error: message };
      }
    },
    [logSipDebug],
  );

  const performAccept = useCallback(async (): Promise<boolean> => {
    logSipDebug('performAccept requested', {
      hasHandle: Boolean(sipHandleRef.current),
      hasIncomingOffer: Boolean(incomingJsepRef.current),
    });
    setIncomingRecoveryState('idle');
    if (!sipHandleRef.current) {
      setLastError('SIP plugin is not attached.');
      pendingAutoAcceptRef.current = false;
      return false;
    }
    if (!incomingJsepRef.current) {
      setLastError('No incoming SIP offer available.');
      pendingAutoAcceptRef.current = false;
      return false;
    }

    try {
      await requestMicrophonePermission();
      logSipDebug('microphone permission granted for SIP accept');
    } catch (error: unknown) {
      setLastError(getApiErrorMessage(error, 'Could not access microphone.'));
      pendingAutoAcceptRef.current = false;
      logSipDebug('microphone permission failed for SIP accept', {
        error: getApiErrorMessage(error, 'Could not access microphone.'),
      });
      return false;
    }

    const incomingJsep = incomingJsepRef.current;
    return new Promise<boolean>((resolve) => {
      try {
        sipHandleRef.current?.createAnswer({
          jsep: incomingJsep,
          tracks: [{ type: 'audio', capture: true, recv: true }],
          success: (localJsep: JanusJS.JSEP) => {
            try {
              sipHandleRef.current?.send({
                message: { request: 'accept' },
                jsep: localJsep,
              });
              logSipDebug('SIP accept sent', { localJsepType: localJsep?.type });
              pendingAutoAcceptRef.current = false;
              setIncomingCall(null);
              statusRef.current = 'in_call';
              setStatus('in_call');
              resolve(true);
            } catch (error: unknown) {
              setLastError(getApiErrorMessage(error, 'Failed to send SIP accept.'));
              pendingAutoAcceptRef.current = false;
              logSipDebug('failed to send SIP accept', {
                error: getApiErrorMessage(error, 'Failed to send SIP accept.'),
              });
              resolve(false);
            }
          },
          error: (error: unknown) => {
            setLastError(getApiErrorMessage(error, 'Could not create SIP answer.'));
            pendingAutoAcceptRef.current = false;
            logSipDebug('createAnswer failed for SIP accept', {
              error: getApiErrorMessage(error, 'Could not create SIP answer.'),
            });
            resolve(false);
          },
        });
      } catch (error: unknown) {
        setLastError(getApiErrorMessage(error, 'Could not accept incoming call.'));
        pendingAutoAcceptRef.current = false;
        logSipDebug('performAccept failed before createAnswer', {
          error: getApiErrorMessage(error, 'Could not accept incoming call.'),
        });
        resolve(false);
      }
    });
  }, [logSipDebug, requestMicrophonePermission]);

  const markInteractionAnswered = useCallback(async () => {
    const candidate = acceptedInteractionCandidateRef.current;
    const callPublicId = String(candidate?.callPublicId || '').trim();
    if (!callPublicId) {
      acceptedInteractionCandidateRef.current = null;
      return;
    }
    if (persistedAnsweredCallIdsRef.current.has(callPublicId)) {
      acceptedInteractionCandidateRef.current = null;
      return;
    }

    try {
      await api.post(`/api/calls/${callPublicId}/mark_interaction_answered/`, {
        session_id: sessionIdRef.current,
        sip_call_id: candidate?.sipCallId || undefined,
      });
      persistedAnsweredCallIdsRef.current.add(callPublicId);
      logSipDebug('mark_interaction_answered success', {
        callId: callPublicId,
        sessionId: sessionIdRef.current,
      });
    } catch (error: unknown) {
      const errorCode = String(getApiErrorField(error, 'error') || '').trim();
      if (errorCode === SIP_INTERACTION_ALREADY_ANSWERED_ERROR) {
        setLastError(SIP_INTERACTION_ALREADY_ANSWERED_ERROR);
        logSipDebug('mark_interaction_answered deferred to existing queue answer owner', {
          callId: callPublicId,
          answeredById: getApiErrorField(error, 'answered_by_id') || null,
          answeredExtension: getApiErrorField(error, 'answered_extension') || null,
        });
        return;
      }
      logSipDebug('mark_interaction_answered failed', {
        callId: callPublicId,
        error: getApiErrorMessage(error, 'Could not persist answered interaction.'),
      });
    } finally {
      acceptedInteractionCandidateRef.current = null;
    }
  }, [logSipDebug]);

  const startRegistration = useCallback(async () => {
    if (!isAuthenticated || !endpointEligible) {
      logSipDebug('startRegistration skipped (not authenticated or not eligible)', {
        isAuthenticated,
        endpointEligible,
      });
      return;
    }
    if (registrationBootstrapBlockedRef.current) {
      logSipDebug('startRegistration skipped (bootstrap blocked after auth failure)');
      applyConnectionState('blocked_auth', {
        reasonCode: 'auth_blocked',
        verifiedAt: null,
      });
      return;
    }
    const currentStatus = statusRef.current;
    const hasActiveRegistration = (
      currentStatus === 'registering'
      || currentStatus === 'registered'
      || currentStatus === 'incoming'
      || currentStatus === 'calling'
      || currentStatus === 'in_call'
    );
    if (startInFlightRef.current || hasActiveRegistration) {
      logSipDebug('startRegistration skipped (already in progress or active)', {
        startInFlight: startInFlightRef.current,
        status: currentStatus,
      });
      return;
    }

    startInFlightRef.current = true;
    registrationStartedAtRef.current = Date.now();
    registrationConfirmedAtRef.current = null;
    statusRef.current = 'registering';
    setStatus('registering');
    setLastError(null);
    applyConnectionState('checking', {
      reasonCode: null,
      verifiedAt: null,
    });
    addOperationalBreadcrumb({
      category: 'sip.registration',
      message: 'SIP registration bootstrap requested',
      level: 'info',
      data: {
        extension: user?.telephonyEndpoint?.extension || null,
        endpointType: user?.telephonyEndpoint?.endpointType || null,
      },
    });
    logSipDebug('startRegistration bootstrap requested');
    try {
      const response = await api.post('/api/calls/sip_session_start/', {});
      const data = response.data as SipSessionStartResponse;
      if (!data?.sessionId || !data?.sipUsername || !data?.sipSecret || !data?.sipProxy) {
        throw new Error('SIP bootstrap payload is incomplete.');
      }
      logSipDebug('startRegistration bootstrap received', {
        sessionId: data.sessionId,
        sipUsername: data.sipUsername,
        sipProxy: data.sipProxy,
        hasIceServers: Array.isArray(data.iceServers) && data.iceServers.length > 0,
        janusAuth: describeJanusAuth(data),
      });

      await initJanus();
      await stopRegistration();
      const janusIceServers: RTCIceServer[] = [...(data.iceServers || [])];
      janusIceServersRef.current = janusIceServers;
      setSessionId(data.sessionId);
      sessionIdRef.current = data.sessionId;
      statusRef.current = 'registering';
      setStatus('registering');
      logSipDebug('creating Janus SIP session', { sessionId: data.sessionId });

      await new Promise<void>((resolve, reject) => {
        const janusLifecycleVersion = janusLifecycleVersionCounterRef.current + 1;
        janusLifecycleVersionCounterRef.current = janusLifecycleVersion;
        activeJanusLifecycleVersionRef.current = janusLifecycleVersion;
        janusRef.current = new Janus({
          server: data.janusWsUrl,
          ...resolveJanusAuthOptions(data),
          iceServers: janusIceServers,
          iceTransportPolicy: 'relay',
          destroyed: () => {
            if (intentionallyDestroyedJanusLifecycleVersionRef.current === janusLifecycleVersion) {
              logSipDebug('janus session destroyed intentionally; recovery suppressed', {
                sessionId: data.sessionId,
                janusLifecycleVersion,
              });
              return;
            }
            if (activeJanusLifecycleVersionRef.current !== janusLifecycleVersion) {
              logSipDebug('janus destroyed callback ignored for stale lifecycle', {
                sessionId: data.sessionId,
                janusLifecycleVersion,
                activeJanusLifecycleVersion: activeJanusLifecycleVersionRef.current,
              });
              return;
            }
            logSipDebug('janus session destroyed');
            if (!isAuthenticated) {
              return;
            }
            applyConnectionState('recovering', {
              reasonCode: 'janus_destroyed',
              verifiedAt: null,
            });
            if (statusRef.current !== 'in_call' && statusRef.current !== 'calling') {
              statusRef.current = 'idle';
              setStatus('idle');
              scheduleSipHealthProbe('janus_destroyed', 250);
            }
          },
          success: () => {
            if (
              activeJanusLifecycleVersionRef.current !== janusLifecycleVersion
              || !janusRef.current
            ) {
              logSipDebug('janus session ready ignored for stale lifecycle', {
                sessionId: data.sessionId,
                janusLifecycleVersion,
                activeJanusLifecycleVersion: activeJanusLifecycleVersionRef.current,
                hasJanus: Boolean(janusRef.current),
              });
              reject(createSipLifecycleCancelledError('Janus lifecycle changed before SIP attach.'));
              return;
            }
            const activeJanus = janusRef.current;
            logSipDebug('janus session ready; attaching SIP plugin');
            activeJanus.attach({
              plugin: 'janus.plugin.sip',
              success: (pluginHandle: JanusJS.PluginHandle) => {
                if (
                  activeJanusLifecycleVersionRef.current !== janusLifecycleVersion
                  || janusRef.current !== activeJanus
                ) {
                  logSipDebug('SIP plugin attach success ignored for stale lifecycle', {
                    sessionId: data.sessionId,
                    janusLifecycleVersion,
                    activeJanusLifecycleVersion: activeJanusLifecycleVersionRef.current,
                  });
                  try {
                    pluginHandle.detach();
                  } catch {
                    // no-op
                  }
                  reject(createSipLifecycleCancelledError('SIP plugin attach resolved after teardown.'));
                  return;
                }
                sipHandleRef.current = pluginHandle;
                try {
                  pluginHandle.send({
                    message: {
                      request: 'register',
                      username: data.sipUsername,
                      authuser: data.sipAuthuser || undefined,
                      secret: data.sipSecret,
                      proxy: data.sipProxy,
                      user_agent: data.sipUserAgent || undefined,
                      incoming_header_prefixes: [SIP_INCOMING_HEADER_PREFIX],
                    },
                  });
                  logSipDebug('SIP register sent', {
                    sipUsername: data.sipUsername,
                    sipProxy: data.sipProxy,
                  });
                  resolve();
                } catch (error: unknown) {
                  logSipDebug('SIP register send failed', {
                    error: getApiErrorMessage(error, 'SIP register send failed.'),
                  });
                  reject(error);
                }
              },
              error: (error: unknown) => {
                if (
                  activeJanusLifecycleVersionRef.current !== janusLifecycleVersion
                  || janusRef.current !== activeJanus
                ) {
                  logSipDebug('SIP plugin attach error ignored for stale lifecycle', {
                    sessionId: data.sessionId,
                    janusLifecycleVersion,
                    activeJanusLifecycleVersion: activeJanusLifecycleVersionRef.current,
                    error: getApiErrorMessage(error, 'SIP plugin attach failed.'),
                  });
                  reject(createSipLifecycleCancelledError('SIP plugin attach failed after teardown.'));
                  return;
                }
                logSipDebug('SIP plugin attach failed', {
                  error: getApiErrorMessage(error, 'SIP plugin attach failed.'),
                });
                reject(error);
              },
              iceState: (nextIceState: string) => {
                const normalizedIceState = String(nextIceState || '').trim().toLowerCase();
                logSipDebug('SIP iceState changed', {
                  iceState: normalizedIceState || 'unknown',
                  status: statusRef.current,
                });
                if (
                  !isAuthenticated
                  || !['disconnected', 'failed', 'closed'].includes(normalizedIceState)
                ) {
                  return;
                }
                applyConnectionState('recovering', {
                  reasonCode: `ice_${normalizedIceState || 'unknown'}`,
                  verifiedAt: null,
                });
                if (statusRef.current !== 'in_call' && statusRef.current !== 'calling') {
                  statusRef.current = 'idle';
                  setStatus('idle');
                  scheduleSipHealthProbe(`ice_state:${normalizedIceState || 'unknown'}`, 0);
                }
              },
              mediaState: (medium: string, on: boolean) => {
                logSipDebug('SIP mediaState changed', {
                  medium,
                  on,
                  status: statusRef.current,
                });
              },
              slowLink: (uplink: boolean, lost: number) => {
                logSipDebug('SIP slowLink detected', {
                  uplink,
                  lost,
                  status: statusRef.current,
                });
              },
              webrtcState: (isUp: boolean) => {
                logSipDebug('SIP webrtcState changed', {
                  isUp,
                  status: statusRef.current,
                });
                if (!isAuthenticated || isUp) {
                  return;
                }
                applyConnectionState('recovering', {
                  reasonCode: 'webrtc_down',
                  verifiedAt: null,
                });
                if (statusRef.current !== 'in_call' && statusRef.current !== 'calling') {
                  statusRef.current = 'idle';
                  setStatus('idle');
                  scheduleSipHealthProbe('webrtc_down', 0);
                }
              },
              onmessage: (message: SipPluginMessage, jsep?: JanusJS.JSEP) => {
                const directErrorReason = (
                  message.error !== null
                  && typeof message.error === 'object'
                  && 'reason' in message.error
                )
                  ? (message.error as { reason?: unknown }).reason
                  : null;
                const errorMessage =
                  directErrorReason
                  || message.error
                  || message.plugindata?.data?.error
                  || message.plugindata?.data?.error_reason;
                const sipResult = message.result || message.plugindata?.data?.result || {};
                const eventName = String(sipResult.event || '').toLowerCase();
                logSipDebug('SIP plugin message', {
                  eventName: eventName || 'unknown',
                  hasJsep: Boolean(jsep),
                  hasError: Boolean(errorMessage),
                  callId: sipResult.call_id || null,
                  code: sipResult.code || null,
                  reason: sipResult.reason || null,
                  hasIncomingHeaders: Boolean(sipResult.headers),
                  rawSipResult: sipResult ?? null,
                  rawSipResultJson: serializeDebugPayload(sipResult),
                });
                if (errorMessage) {
                  pendingAutoAcceptRef.current = false;
                  setIncomingRecoveryState('idle');
                  statusRef.current = 'error';
                  setStatus('error');
                  setLastError(String(errorMessage));
                  return;
                }

                if (jsep && eventName !== 'incomingcall') {
                  try {
                    sipHandleRef.current?.handleRemoteJsep({ jsep });
                    logSipDebug('remote JSEP handled', { eventName, jsepType: jsep?.type });
                  } catch {
                    // no-op
                    logSipDebug('remote JSEP handle failed', { eventName });
                  }
                }

      if (eventName === 'registered') {
                  registrationConfirmedAtRef.current = Date.now();
                  statusRef.current = 'registered';
                  setStatus('registered');
                  setLastError(null);
                  applyConnectionState('checking', {
                    reasonCode: null,
                    verifiedAt: null,
                  });
                  scheduleSipHealthProbe('registered_event', 1_000);
                  return;
                }
                if (eventName === 'registration_failed') {
                  addOperationalBreadcrumb({
                    category: 'sip.registration',
                    message: 'SIP registration failed',
                    level: 'warning',
                    data: {
                      extension: user?.telephonyEndpoint?.extension || null,
                      reason: sipResult.reason || null,
                      code: sipResult.code || null,
                    },
                  });
                  pendingAutoAcceptRef.current = false;
                  setIncomingRecoveryState('idle');
                  statusRef.current = 'error';
                  setStatus('error');
                  setLastError(String(sipResult.reason || 'SIP registration failed.'));
                  applyConnectionState('error', {
                    reasonCode: 'registration_failed',
                    verifiedAt: null,
                  });
                  return;
                }
                if (eventName === 'incomingcall') {
                  const sipHeaders = sipResult.headers;
                  const callPublicId = getIncomingHeaderValue(sipHeaders, SIP_INCOMING_HEADER_CALL_ID);
                  const tenantPublicId = getIncomingHeaderValue(sipHeaders, SIP_INCOMING_HEADER_TENANT_ID);
                  logSipDebug('incomingcall headers parsed', {
                    hasHeaders: Boolean(sipHeaders && typeof sipHeaders === 'object'),
                    callPublicId,
                    tenantPublicId,
                    headers: getIncomingHeadersDebugSnapshot(sipHeaders),
                    headersRaw: sipHeaders ?? null,
                    headersRawJson: serializeDebugPayload(sipHeaders),
                  });
                  const incomingSnapshot = {
                    receivedAt: new Date().toISOString(),
                    hasOffer: Boolean(jsep),
                    event: eventName,
                    sipCallId: String(sipResult.call_id || '').trim() || null,
                    callPublicId,
                    tenantPublicId,
                  } satisfies SipIncomingCall;
                  incomingJsepRef.current = jsep ?? null;
                  acceptedInteractionCandidateRef.current = incomingSnapshot;
                  trackedCallPublicIdRef.current = incomingSnapshot.callPublicId;
                  setIncomingCall(incomingSnapshot);
                  addOperationalBreadcrumb({
                    category: 'sip.incoming',
                    message: 'Incoming SIP call delivered',
                    level: 'info',
                    data: {
                      callPublicId: incomingSnapshot.callPublicId,
                      tenantPublicId: incomingSnapshot.tenantPublicId,
                      sipCallId: incomingSnapshot.sipCallId,
                      hasOffer: incomingSnapshot.hasOffer,
                    },
                  });
                  statusRef.current = 'incoming';
                  setStatus('incoming');
                  if (pendingAutoAcceptRef.current && !acceptInFlightRef.current) {
                    void performAccept();
                  }
                  return;
                }
                if (eventName === 'progress' || eventName === 'calling') {
                  statusRef.current = 'calling';
                  setStatus('calling');
                  return;
                }
                if (eventName === 'accepted') {
                  trackedCallPublicIdRef.current = String(
                    acceptedInteractionCandidateRef.current?.callPublicId
                    || trackedCallPublicIdRef.current
                    || '',
                  ).trim() || null;
                  setIncomingCall(null);
                  pendingAutoAcceptRef.current = false;
                  setIncomingRecoveryState('idle');
                  statusRef.current = 'in_call';
                  setStatus('in_call');
                  void markInteractionAnswered();
                  return;
                }
                if (eventName === 'hangup') {
                  clearTrackedCallState('registered');
                }
              },
              onremotetrack: (track: MediaStreamTrack, _mid: string, on: boolean) => {
                if (track.kind !== 'audio') {
                  return;
                }
                if (!remoteStreamRef.current) {
                  remoteStreamRef.current = new MediaStream();
                }
                if (on) {
                  remoteStreamRef.current.addTrack(track);
                } else {
                  remoteStreamRef.current.removeTrack(track);
                  if (remoteStreamRef.current.getAudioTracks().length === 0) {
                    remoteStreamRef.current = null;
                  }
                }
                const snapshot = remoteStreamRef.current
                  ? new MediaStream(remoteStreamRef.current.getAudioTracks())
                  : null;
                setRemoteAudioStream(snapshot);
                logSipDebug('SIP remote audio track update', {
                  trackId: track.id,
                  on,
                  readyState: track.readyState,
                  streamTracks: remoteStreamRef.current?.getAudioTracks().length || 0,
                });
                try {
                  const element = ensureAudioElement();
                  element.srcObject = remoteStreamRef.current;
                  if (remoteStreamRef.current) {
                    void element.play();
                  }
                } catch {
                  // no-op
                }
              },
              oncleanup: () => {
                if (intentionallyDestroyedJanusLifecycleVersionRef.current === janusLifecycleVersion) {
                  logSipDebug('SIP plugin cleanup ignored after intentional teardown', {
                    sessionId: data.sessionId,
                    janusLifecycleVersion,
                  });
                  return;
                }
                if (activeJanusLifecycleVersionRef.current !== janusLifecycleVersion) {
                  logSipDebug('SIP plugin cleanup ignored for stale lifecycle', {
                    sessionId: data.sessionId,
                    janusLifecycleVersion,
                    activeJanusLifecycleVersion: activeJanusLifecycleVersionRef.current,
                  });
                  return;
                }
                incomingJsepRef.current = null;
                acceptedInteractionCandidateRef.current = null;
                pendingAutoAcceptRef.current = false;
                acceptInFlightRef.current = null;
                trackedCallPublicIdRef.current = null;
                setIncomingRecoveryState('idle');
                setIncomingCall(null);
                setIsMuted(false);
                cleanupLocalAudioSenders();
                cleanupAudio();
                logSipDebug('SIP plugin cleanup');
                addOperationalBreadcrumb({
                  category: 'sip.plugin',
                  message: 'SIP plugin cleanup triggered',
                  level: 'warning',
                  data: {
                    extension: user?.telephonyEndpoint?.extension || null,
                    status: statusRef.current,
                  },
                });
                if (isAuthenticated) {
                  applyConnectionState('recovering', {
                    reasonCode: 'plugin_cleanup',
                    verifiedAt: null,
                  });
                  if (statusRef.current !== 'in_call' && statusRef.current !== 'calling') {
                    statusRef.current = 'idle';
                    setStatus('idle');
                    scheduleSipHealthProbe('plugin_cleanup', 500);
                  }
                }
              },
            } as unknown as JanusJS.PluginOptions);
          },
          error: (error: unknown) => {
            logSipDebug('janus init failed', {
              error: getApiErrorMessage(error, 'Could not initialize SIP registration.'),
            });
            reject(error);
          },
        });
      });
    } catch (error: unknown) {
      if (isSipLifecycleCancelledError(error)) {
        logSipDebug('startRegistration cancelled by lifecycle change', {
          reason: error.message,
          extension: user?.telephonyEndpoint?.extension || null,
        });
        return;
      }
      const statusCode = getHttpStatusCode(error);
      const errorMessage = getApiErrorMessage(error, 'Could not initialize SIP registration.');
      if (statusCode === 401 || statusCode === 403) {
        registrationBootstrapBlockedRef.current = true;
        await stopRegistration({ preserveError: true, nextStatus: 'error' });
        statusRef.current = 'error';
        setStatus('error');
        setLastError(errorMessage);
        applyConnectionState('blocked_auth', {
          reasonCode: 'auth_blocked',
          verifiedAt: null,
        });
        logSipDebug('startRegistration blocked after auth failure', {
          statusCode,
          error: errorMessage,
        });
        captureFrontendException(error, {
          level: 'warning',
          fingerprint: ['sip-registration-auth-blocked'],
          tags: {
            operational_surface: 'sip',
            sip_extension: user?.telephonyEndpoint?.extension || null,
          },
          contexts: {
            sip_registration: {
              status_code: statusCode,
              extension: user?.telephonyEndpoint?.extension || null,
              endpoint_type: user?.telephonyEndpoint?.endpointType || null,
              phase: 'bootstrap_or_janus_init',
              auth_blocked: true,
            },
          },
        });
        return;
      }
      setStatus('error');
      statusRef.current = 'error';
      setLastError(errorMessage);
      applyConnectionState('error', {
        reasonCode: 'bootstrap_failed',
        verifiedAt: null,
      });
      logSipDebug('startRegistration failed', {
        error: errorMessage,
      });
      captureFrontendException(error, {
        level: 'error',
        fingerprint: ['sip-start-registration-failed'],
        tags: {
          operational_surface: 'sip',
          sip_extension: user?.telephonyEndpoint?.extension || null,
        },
        contexts: {
          sip_registration: {
            status_code: statusCode,
            extension: user?.telephonyEndpoint?.extension || null,
            endpoint_type: user?.telephonyEndpoint?.endpointType || null,
            phase: 'bootstrap_or_janus_init',
            connection_state: connectionState,
          },
        },
      });
      await stopRegistration({ preserveError: true, nextStatus: 'error' });
    } finally {
      startInFlightRef.current = false;
    }
  }, [applyConnectionState, clearTrackedCallState, cleanupAudio, cleanupLocalAudioSenders, connectionState, endpointEligible, ensureAudioElement, initJanus, isAuthenticated, logSipDebug, markInteractionAnswered, performAccept, scheduleSipHealthProbe, stopRegistration, user?.telephonyEndpoint?.endpointType, user?.telephonyEndpoint?.extension]);

  const clearIncoming = useCallback(() => {
    incomingJsepRef.current = null;
    acceptedInteractionCandidateRef.current = null;
    pendingAutoAcceptRef.current = false;
    acceptInFlightRef.current = null;
    trackedCallPublicIdRef.current = null;
    setIncomingRecoveryState('idle');
    setIncomingCall(null);
    if (status === 'incoming') {
      statusRef.current = 'registered';
      setStatus('registered');
    }
  }, [status]);

  const reconcileTerminalCallState = useCallback((callPublicId?: string | null, options?: { force?: boolean }) => {
    const normalizedCallId = String(callPublicId || '').trim();
    const trackedCallId = String(trackedCallPublicIdRef.current || '').trim();
    const currentStatus = statusRef.current;
    const force = options?.force === true;

    if (!normalizedCallId) {
      return;
    }
    if (currentStatus !== 'incoming' && currentStatus !== 'calling' && currentStatus !== 'in_call') {
      return;
    }
    if (!force && (!trackedCallId || normalizedCallId !== trackedCallId)) {
      return;
    }

    logSipDebug('external call terminal reconciliation applied', {
      callPublicId: normalizedCallId,
      trackedCallId: trackedCallId || null,
      currentStatus,
      force,
    });
    if (sipHandleRef.current) {
      try {
        sipHandleRef.current.send({ message: { request: 'hangup' } });
      } catch {
        // no-op
      }
    }
    clearTrackedCallState('registered');
  }, [clearTrackedCallState, logSipDebug]);

  const keepSipBrowserSessionAlive = useCallback(
    async (sessionIdToKeep: string, reason: string): Promise<void> => {
      const response = await api.post('/api/calls/sip_session_keepalive/', { session_id: sessionIdToKeep });
      keepaliveFailureCountRef.current = 0;
      refreshJanusIceServers(response?.data as TurnIceServersRefreshPayload | undefined, 'keepalive');
      logSipDebug('SIP keepalive ok', {
        reason,
        sessionId: sessionIdToKeep,
      });
    },
    [logSipDebug, refreshJanusIceServers],
  );

  const triggerSipSessionRecovery = useCallback(
    async (reason: string, options?: { force?: boolean }) => {
      if (!isAuthenticated || !endpointEligible) {
        return;
      }
      if (recoveryInFlightRef.current) {
        logSipDebug('SIP recovery skipped (already in flight)', { reason });
        return;
      }
      const nowMs = Date.now();
      const elapsedSinceLastRestart = nowMs - recoveryLastStartedAtRef.current;
      if (!options?.force && elapsedSinceLastRestart < SIP_RECOVERY_COOLDOWN_MS) {
        logSipDebug('SIP recovery skipped (cooldown)', {
          reason,
          cooldownMs: SIP_RECOVERY_COOLDOWN_MS,
          elapsedMs: elapsedSinceLastRestart,
        });
        return;
      }

      recoveryInFlightRef.current = true;
      recoveryLastStartedAtRef.current = nowMs;
      keepaliveFailureCountRef.current = 0;
      applyConnectionState('recovering', { reasonCode: reason });
      addOperationalBreadcrumb({
        category: 'sip.recovery',
        message: 'SIP recovery started',
        level: 'warning',
        data: {
          reason,
          extension: user?.telephonyEndpoint?.extension || null,
          force: options?.force === true,
        },
      });
      logSipDebug('SIP recovery started', { reason });

      try {
        await stopRegistration();
      } catch (error: unknown) {
        logSipDebug('SIP recovery stopRegistration failed', {
          reason,
          error: getApiErrorMessage(error, 'stopRegistration failed'),
        });
        captureFrontendException(error, {
          level: 'warning',
          fingerprint: ['sip-recovery-stop-registration-failed'],
          tags: {
            operational_surface: 'sip',
            sip_extension: user?.telephonyEndpoint?.extension || null,
          },
          contexts: {
            sip_recovery: {
              reason,
              phase: 'stop_registration',
            },
          },
        });
      }

      try {
        await startRegistration();
      } finally {
        recoveryInFlightRef.current = false;
      }
    },
    [applyConnectionState, endpointEligible, isAuthenticated, logSipDebug, startRegistration, stopRegistration, user?.telephonyEndpoint?.extension],
  );

  const probeSipSessionHealth = useCallback(
    async (reason: string) => {
      if (!isEligible) {
        applyConnectionState('unknown', { reasonCode: null, verifiedAt: null });
        return;
      }

      const currentStatus = statusRef.current;
      const activeSessionId = sessionIdRef.current;
      if (!activeSessionId) {
        applyConnectionState('disconnected', {
          reasonCode: 'session_not_found',
          verifiedAt: null,
        });
        if (!isRegistered && !isRegistering && !startInFlightRef.current) {
          logSipDebug('SIP probe starting registration (missing session)', { reason });
          await startRegistration();
        }
        return;
      }

      const isActiveCallState = currentStatus === 'incoming' || currentStatus === 'calling' || currentStatus === 'in_call';
      try {
        if (isActiveCallState) {
          await keepSipBrowserSessionAlive(activeSessionId, reason);
          applyConnectionState('connected', {
            reasonCode: null,
            verifiedAt: new Date().toISOString(),
          });
          return;
        }

        const response = await api.post('/api/calls/sip_session_readiness/', {
          session_id: activeSessionId,
        });
        const readiness = response.data as SipSessionReadinessResponse;
        keepaliveFailureCountRef.current = 0;
        refreshJanusIceServers(response.data as TurnIceServersRefreshPayload, 'readiness');
        logSipDebug('SIP readiness probe ok', {
          reason,
          sessionId: activeSessionId,
          status: readiness.status,
          sessionStatus: readiness.sessionStatus,
          endpointStatus: readiness.endpointStatus,
          sipContactStatus: readiness.sipContactStatus,
          reasonCode: readiness.reasonCode,
          extension: readiness.extension,
        });

        if (readiness.status === 'ok' && readiness.sipContactStatus === 'present') {
          applyConnectionState('connected', {
            reasonCode: null,
            verifiedAt: readiness.verifiedAt,
          });
          return;
        }

        const nowMs = Date.now();
        const isWithinVerificationGraceWindow = (
          currentStatus === 'registering'
          || (
            registrationStartedAtRef.current != null
            && nowMs - registrationStartedAtRef.current < SIP_CONTACT_VERIFICATION_GRACE_MS
          )
          || (
            registrationConfirmedAtRef.current != null
            && nowMs - registrationConfirmedAtRef.current < SIP_CONTACT_VERIFICATION_GRACE_MS
          )
        );
        if (isWithinVerificationGraceWindow) {
          applyConnectionState('checking', {
            reasonCode: readiness.reasonCode,
            verifiedAt: readiness.verifiedAt,
          });
          logSipDebug('SIP readiness probe deferred recovery during grace window', {
            reason,
            currentStatus,
            reasonCode: readiness.reasonCode,
            sipContactStatus: readiness.sipContactStatus,
          });
          return;
        }

        applyConnectionState('disconnected', {
          reasonCode: readiness.reasonCode,
          verifiedAt: readiness.verifiedAt,
        });
        if (
          readiness.reasonCode === 'sip_contact_missing'
          || readiness.reasonCode === 'sip_contact_unavailable'
        ) {
          addOperationalBreadcrumb({
            category: 'sip.readiness',
            message: 'SIP contact unreachable; recovery requested',
            level: 'warning',
            data: {
              reason,
              extension: readiness.extension,
            },
          });
          await triggerSipSessionRecovery(`${reason}:${readiness.reasonCode}`);
        }
      } catch (error: unknown) {
        const statusCode = getHttpStatusCode(error);
        logSipDebug('SIP session health probe failed', {
          reason,
          sessionId: activeSessionId,
          statusCode,
          error: getApiErrorMessage(error, 'SIP session health probe failed'),
        });
        if (statusCode === 401 || statusCode === 403) {
          applyConnectionState('blocked_auth', {
            reasonCode: 'auth_blocked',
            verifiedAt: null,
          });
          return;
        }
        if (statusCode === 404) {
          applyConnectionState('disconnected', {
            reasonCode: 'session_not_found',
            verifiedAt: null,
          });
          await triggerSipSessionRecovery(`${reason}:session_not_found`, { force: true });
          return;
        }
        keepaliveFailureCountRef.current += 1;
        applyConnectionState('error', {
          reasonCode: 'probe_failed',
          verifiedAt: null,
        });
        if (keepaliveFailureCountRef.current >= SIP_KEEPALIVE_FAILURE_THRESHOLD) {
          captureOperationalIssue('SIP session health probe reached failure threshold', {
            level: 'warning',
            fingerprint: ['sip-health-probe-threshold-reached'],
            tags: {
              operational_surface: 'sip',
              sip_extension: user?.telephonyEndpoint?.extension || null,
            },
            contexts: {
              sip_probe: {
                reason,
                status_code: statusCode,
                failure_count: keepaliveFailureCountRef.current,
                threshold: SIP_KEEPALIVE_FAILURE_THRESHOLD,
                active_call_state: isActiveCallState,
              },
            },
          });
        }
        if (!isActiveCallState && keepaliveFailureCountRef.current >= SIP_KEEPALIVE_FAILURE_THRESHOLD) {
          await triggerSipSessionRecovery(`${reason}:probe_failures`);
        }
      }
    },
    [
      applyConnectionState,
      isEligible,
      isRegistered,
      isRegistering,
      keepSipBrowserSessionAlive,
      logSipDebug,
      refreshJanusIceServers,
      startRegistration,
      triggerSipSessionRecovery,
      user?.telephonyEndpoint?.extension,
    ],
  );

  useEffect(() => {
    probeSipSessionHealthRef.current = probeSipSessionHealth;
  }, [probeSipSessionHealth]);

  useEffect(() => {
    if (
      typeof document === 'undefined'
      || discardedRecoveryHandledRef.current
      || !isDocumentDiscarded(document)
      || !isEligible
    ) {
      return;
    }
    discardedRecoveryHandledRef.current = true;
    logSipDebug('document.wasDiscarded detected; forcing SIP health revalidation');
    addOperationalBreadcrumb({
      category: 'sip.lifecycle',
      message: 'Browser discarded the document; forcing SIP health revalidation',
      level: 'warning',
      data: {
        extension: user?.telephonyEndpoint?.extension || null,
      },
    });
    applyConnectionState('recovering', {
      reasonCode: 'document_discarded',
      verifiedAt: null,
    });
    scheduleSipHealthProbe('document_discarded', 0);
  }, [applyConnectionState, isEligible, logSipDebug, scheduleSipHealthProbe, user?.telephonyEndpoint?.extension]);

  const acceptIncoming = useCallback(
    async (
      callId?: string,
      options?: { retryIncomingDelivery?: boolean; deferUntilOffer?: boolean },
    ): Promise<boolean> => {
      logSipDebug('acceptIncoming requested', {
        hasSession: Boolean(sessionIdRef.current),
        hasIncomingOffer: Boolean(incomingJsepRef.current),
        callId: callId || null,
        retryIncomingDelivery: options?.retryIncomingDelivery !== false,
        deferUntilOffer: options?.deferUntilOffer === true,
      });
      if (acceptInFlightRef.current) {
        logSipDebug('acceptIncoming already in progress; joining existing attempt');
        return acceptInFlightRef.current;
      }

      const runAccept = async (): Promise<boolean> => {
        const currentSessionId = sessionIdRef.current;
        if (!currentSessionId) {
          setLastError('SIP session is not ready.');
          setIncomingRecoveryState('idle');
          return false;
        }
        setLastError(null);
        pendingAutoAcceptRef.current = true;

        if (incomingJsepRef.current) {
          setIncomingRecoveryState('idle');
          return performAccept();
        }

        if (options?.deferUntilOffer === true) {
          setIncomingRecoveryState('idle');
          logSipDebug('acceptIncoming armed waiting future incoming offer');
          return true;
        }

        setIncomingRecoveryState('waiting_offer');
        logSipDebug('acceptIncoming queued waiting offer');
        const hasOfferAfterWait = await waitForIncomingOffer(1500);
        if (hasOfferAfterWait && incomingJsepRef.current) {
          setIncomingRecoveryState('idle');
          return performAccept();
        }

        const shouldRetryIncomingDelivery = options?.retryIncomingDelivery !== false;
        if (!callId || !shouldRetryIncomingDelivery) {
          pendingAutoAcceptRef.current = false;
          setIncomingRecoveryState('idle');
          setLastError('No incoming SIP offer available.');
          return false;
        }

        setIncomingRecoveryState('retrying_delivery');
        const activationResult = await requestIncomingDeliveryRetry(callId, currentSessionId);
        if (!activationResult.ok) {
          pendingAutoAcceptRef.current = false;
          setIncomingRecoveryState('idle');
          setLastError(activationResult.error || 'Could not retry incoming call delivery.');
          return false;
        }

        const hasOfferAfterRetry = await waitForIncomingOffer(4500);
        if (hasOfferAfterRetry && incomingJsepRef.current) {
          setIncomingRecoveryState('idle');
          return performAccept();
        }

        pendingAutoAcceptRef.current = false;
        setIncomingRecoveryState('idle');
        setLastError('Incoming SIP offer was not delivered yet. Please try again.');
        return false;
      };

      const acceptPromise = runAccept().finally(() => {
        acceptInFlightRef.current = null;
      });
      acceptInFlightRef.current = acceptPromise;
      return acceptPromise;
    },
    [logSipDebug, performAccept, requestIncomingDeliveryRetry, waitForIncomingOffer],
  );

  const rejectIncoming = useCallback(async (): Promise<boolean> => {
    logSipDebug('rejectIncoming requested', {
      hasHandle: Boolean(sipHandleRef.current),
    });
    if (!sipHandleRef.current) {
      setLastError('SIP plugin is not attached.');
      return false;
    }
    try {
      sipHandleRef.current.send({ message: { request: 'decline' } });
      logSipDebug('SIP decline sent');
      clearIncoming();
      return true;
    } catch (error: unknown) {
      setLastError(getApiErrorMessage(error, 'Could not reject incoming call.'));
      logSipDebug('SIP decline failed', {
        error: getApiErrorMessage(error, 'Could not reject incoming call.'),
      });
      return false;
    }
  }, [clearIncoming, logSipDebug]);

  const hold = useCallback(async (direction: SipHoldDirection = 'sendonly'): Promise<boolean> => {
    logSipDebug('hold requested', {
      hasHandle: Boolean(sipHandleRef.current),
      status: statusRef.current,
    });
    const currentStatus = statusRef.current;
    if (currentStatus !== 'in_call' && currentStatus !== 'calling') {
      setLastError('SIP call is not active.');
      return false;
    }
    if (!sipHandleRef.current) {
      setLastError('SIP plugin is not attached.');
      return false;
    }
    const sipHandle = sipHandleRef.current;
    try {
      const normalizedDirection = direction === 'sendonly' || direction === 'recvonly' || direction === 'inactive'
        ? direction
        : 'sendonly';
      await new Promise<void>((resolve, reject) => {
        sipHandle.send({
          message: { request: 'hold', direction: normalizedDirection },
          success: () => resolve(),
          error: (error: unknown) => reject(error),
        });
      });
      logSipDebug('SIP hold sent', {
        direction: normalizedDirection,
      });
      return true;
    } catch (error: unknown) {
      setLastError(getApiErrorMessage(error, 'Could not put call on hold.'));
      logSipDebug('SIP hold failed', {
        error: getApiErrorMessage(error, 'Could not put call on hold.'),
      });
      return false;
    }
  }, [logSipDebug]);

  const unhold = useCallback(async (): Promise<boolean> => {
    logSipDebug('unhold requested', {
      hasHandle: Boolean(sipHandleRef.current),
      status: statusRef.current,
    });
    const currentStatus = statusRef.current;
    if (currentStatus !== 'in_call' && currentStatus !== 'calling') {
      setLastError('SIP call is not active.');
      return false;
    }
    if (!sipHandleRef.current) {
      setLastError('SIP plugin is not attached.');
      return false;
    }
    const sipHandle = sipHandleRef.current;
    try {
      await new Promise<void>((resolve, reject) => {
        sipHandle.send({
          message: { request: 'unhold' },
          success: () => resolve(),
          error: (error: unknown) => reject(error),
        });
      });
      logSipDebug('SIP unhold sent');
      return true;
    } catch (error: unknown) {
      setLastError(getApiErrorMessage(error, 'Could not resume call.'));
      logSipDebug('SIP unhold failed', {
        error: getApiErrorMessage(error, 'Could not resume call.'),
      });
      return false;
    }
  }, [logSipDebug]);

  /**
   * Apply local microphone state on active SIP WebRTC audio senders.
   *
   * This toggles uplink only (`sender.track.enabled`) and does not affect
   * remote/downlink audio playback.
   */
  const applyMicrophoneEnabled = useCallback((enabled: boolean): boolean => {
    const pc: RTCPeerConnection | null = sipHandleRef.current?.webrtcStuff?.pc ?? null;
    if (!pc || typeof pc.getSenders !== 'function') {
      return false;
    }
    const audioSenders = pc.getSenders().filter((sender) => sender.track?.kind === 'audio');
    if (audioSenders.length === 0) {
      return false;
    }
    audioSenders.forEach((sender) => {
      if (sender.track) {
        sender.track.enabled = enabled;
      }
    });
    return true;
  }, []);

  const mute = useCallback(async (): Promise<boolean> => {
    logSipDebug('mute requested', {
      hasHandle: Boolean(sipHandleRef.current),
      status: statusRef.current,
      alreadyMuted: isMuted,
    });
    const currentStatus = statusRef.current;
    if (currentStatus !== 'in_call' && currentStatus !== 'calling') {
      setLastError('SIP call is not active.');
      return false;
    }
    if (!sipHandleRef.current) {
      setLastError('SIP plugin is not attached.');
      return false;
    }
    if (isMuted) {
      return true;
    }
    const applied = applyMicrophoneEnabled(false);
    if (!applied) {
      setLastError('Could not mute local microphone.');
      logSipDebug('mute failed (no local audio sender)');
      return false;
    }
    setIsMuted(true);
    setLastError(null);
    logSipDebug('mute applied');
    return true;
  }, [applyMicrophoneEnabled, isMuted, logSipDebug]);

  const unmute = useCallback(async (): Promise<boolean> => {
    logSipDebug('unmute requested', {
      hasHandle: Boolean(sipHandleRef.current),
      status: statusRef.current,
      alreadyMuted: isMuted,
    });
    const currentStatus = statusRef.current;
    if (currentStatus !== 'in_call' && currentStatus !== 'calling') {
      setLastError('SIP call is not active.');
      return false;
    }
    if (!sipHandleRef.current) {
      setLastError('SIP plugin is not attached.');
      return false;
    }
    if (!isMuted) {
      return true;
    }
    const applied = applyMicrophoneEnabled(true);
    if (!applied) {
      setLastError('Could not unmute local microphone.');
      logSipDebug('unmute failed (no local audio sender)');
      return false;
    }
    setIsMuted(false);
    setLastError(null);
    logSipDebug('unmute applied');
    return true;
  }, [applyMicrophoneEnabled, isMuted, logSipDebug]);

  const hangup = useCallback(async () => {
    logSipDebug('hangup requested', {
      hasHandle: Boolean(sipHandleRef.current),
    });
    if (!sipHandleRef.current) {
      return;
    }
    try {
      sipHandleRef.current.send({ message: { request: 'hangup' } });
      logSipDebug('SIP hangup sent');
    } catch {
      // no-op
      logSipDebug('SIP hangup send failed');
    }
  }, [logSipDebug]);

  useEffect(() => {
    if (!sessionId) {
      stopKeepalive();
      heartbeatLastRunAtRef.current = null;
      keepaliveFailureCountRef.current = 0;
      return undefined;
    }
    stopKeepalive();
    const runHeartbeat = async (reason: string) => {
      const nowMs = Date.now();
      const previousRunAt = heartbeatLastRunAtRef.current;
      heartbeatLastRunAtRef.current = nowMs;
      if (previousRunAt != null) {
        const elapsedMs = nowMs - previousRunAt;
        if (elapsedMs > SIP_SUSPENSION_DRIFT_THRESHOLD_MS) {
          logSipDebug('SIP heartbeat detected browser suspension/throttling drift', {
            reason,
            elapsedMs,
            thresholdMs: SIP_SUSPENSION_DRIFT_THRESHOLD_MS,
          });
          applyConnectionState('recovering', {
            reasonCode: 'browser_suspended',
            verifiedAt: null,
          });
        }
      }
      await probeSipSessionHealth(reason);
    };
    void runHeartbeat('interval_bootstrap');
    keepaliveRef.current = window.setInterval(() => {
      void runHeartbeat('interval');
    }, SIP_KEEPALIVE_INTERVAL_MS);
    return () => stopKeepalive();
  }, [applyConnectionState, logSipDebug, probeSipSessionHealth, sessionId, stopKeepalive]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return undefined;
    }

    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      void probeSipSessionHealth('visibilitychange');
    };
    const onWindowFocus = () => {
      void probeSipSessionHealth('window_focus');
    };
    const onOnline = () => {
      void probeSipSessionHealth('network_online');
    };
    const onPageShow = () => {
      void probeSipSessionHealth('pageshow');
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onWindowFocus);
    window.addEventListener('online', onOnline);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onWindowFocus);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [probeSipSessionHealth]);

  useEffect(() => {
    if (status === 'in_call' || status === 'calling') {
      return;
    }
    setIsMuted(false);
  }, [status]);

  useEffect(() => {
    if (!registrationIdentity) {
      registrationIdentityRef.current = null;
      registrationBootstrapBlockedRef.current = false;
      applyConnectionState('unknown', {
        reasonCode: null,
        verifiedAt: null,
      });
      if (isRegistered || isRegistering || Boolean(sessionIdRef.current) || startInFlightRef.current) {
        void stopRegistration();
      }
      return;
    }

    if (
      registrationIdentityRef.current
      && registrationIdentityRef.current !== registrationIdentity
      && (isRegistered || isRegistering || Boolean(sessionIdRef.current))
    ) {
      void stopRegistration();
    }
    if (registrationIdentityRef.current !== registrationIdentity) {
      registrationBootstrapBlockedRef.current = false;
    }
    registrationIdentityRef.current = registrationIdentity;

    if (!isRegistered && !isRegistering && !startInFlightRef.current) {
      void startRegistration();
    }
  }, [applyConnectionState, isRegistered, isRegistering, registrationIdentity, startRegistration, stopRegistration]);

  useEffect(() => () => {
    void stopRegistration();
  }, [stopRegistration]);

  const value = useMemo<SipExtensionContextValue>(
    () => ({
      isEligible,
      status,
      connectionState,
      connectionReasonCode,
      lastVerifiedAt,
      isRegistered,
      isRegistering,
      lastError,
      sessionId,
      incomingCall,
      incomingRecoveryState,
      remoteAudioStream,
      isMuted,
      startRegistration,
      stopRegistration,
      acceptIncoming,
      rejectIncoming,
      hangup,
      hold,
      unhold,
      mute,
      unmute,
      reconcileTerminalCallState,
      clearIncoming,
    }),
    [
      acceptIncoming,
      clearIncoming,
      hangup,
      hold,
      incomingCall,
      incomingRecoveryState,
      isMuted,
      isEligible,
      connectionReasonCode,
      connectionState,
      isRegistered,
      isRegistering,
      lastVerifiedAt,
      lastError,
      mute,
      remoteAudioStream,
      reconcileTerminalCallState,
      rejectIncoming,
      sessionId,
      startRegistration,
      status,
      stopRegistration,
      unmute,
      unhold,
    ],
  );

  return <SipExtensionContext.Provider value={value}>{children}</SipExtensionContext.Provider>;
}

export function useSipExtension() {
  const context = useContext(SipExtensionContext);
  if (!context) {
    throw new Error('useSipExtension must be used within TelephonySipProvider');
  }
  return context;
}
